import { PlusOutlined } from '@icon';
import { memberApiV2 } from '@lib/api';
import {
  IconButton,
  Table,
  TableProps,
  TTableInitialState,
  Typography,
  useIsAuthorized,
} from '@ui';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { TAuthority } from 'types/authority.types';
import { Full } from 'types/common';
import TStore from '../../../../types/store';
import AddEditStaff from './components/AddEditStuff';
import ApproveStaffModal from './components/ApproveStaffModal';
import { StyledStaffWrapper } from './Staff.styled';
import { IStaffPaginationArgs, useTableColumns } from './Staff.utils';

const EDIT_AUTH: TAuthority[] = ['workerManagementEdit'];

type TStaffModal = {
  open: boolean;
  type: 'add' | 'edit';
  record?: any;
};

type TTableProps = Full<TableProps<any>>;

const INITIAL_PARAMS: IStaffPaginationArgs = {
  page: 1,
  pageSize: 30,
  sortBy: 'createdAt',
  direction: 'DESC',
};

const INITIAL_STATE: TTableInitialState<any> = {
  pageSize: 30,
  pageIndex: 0,
  sortBy: [{ id: 'createdAt', desc: true }],
};

const Staff: React.FC = () => {
  const { formatMessage } = useIntl();
  const canEdit = useIsAuthorized(EDIT_AUTH);

  const providerId = useSelector((state: TStore) => state.users.business[0].id);

  const [addEditStaffModalState, setAddEditStaffModalState] = useState<TStaffModal>({
    open: false,
    type: 'add',
  });

  const [approveStaffModalOpen, setApproveStaffModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useState<IStaffPaginationArgs>(INITIAL_PARAMS);

  const { data, refetch, isFetching } = useQuery(
    ['searchStaff', searchParams, providerId],
    async () => {
      const { data: axData } = await memberApiV2.searchStaff(providerId as string, searchParams);
      return axData.data;
    },
  );

  const onApproveClick = React.useCallback((record: any) => {
    setApproveStaffModalOpen(true);
    setAddEditStaffModalState({
      open: false,
      type: 'add',
      record,
    });
  }, []);

  const onEditClick = React.useCallback((record: any) => {
    setAddEditStaffModalState({
      open: true,
      type: 'edit',
      record,
    });
  }, []);

  const onDeleteClick = React.useCallback((record: any) => {}, []);

  const columns = useTableColumns({ onApproveClick, onEditClick, onDeleteClick });

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    setSearchParams((o) => ({
      ...o,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: sortBy as any,
      direction: sortDirection as any,
    }));
  }, []);

  return (
    <StyledStaffWrapper>
      <AddEditStaff
        onRequestClose={() => {
          setAddEditStaffModalState({ open: false, type: 'add' });
          refetch();
        }}
        staffData={addEditStaffModalState.record}
        visible={addEditStaffModalState.open}
      />
      {approveStaffModalOpen && (
        <ApproveStaffModal
          id={addEditStaffModalState.record.id}
          onRequestClose={() => setApproveStaffModalOpen(false)}
        />
      )}
      {/* <DeleteModal /> */}
      <Table.Header
        title={
          (
            <Typography display="h5" as="span">
              {`${formatMessage({ id: 'staff_table_title' })}` + ` (${data?.pagination.total})`}
            </Typography>
          ) as any
        }
        extra={
          canEdit ? (
            <IconButton
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                setAddEditStaffModalState({
                  open: true,
                  type: 'add',
                });
              }}
            />
          ) : null
        }
      />
      <Table
        loading={isFetching}
        manualSortBy
        disableSortBy={false}
        columns={columns}
        data={data?.items ?? []}
        pagination={data?.pagination}
        onChange={handleTableChange}
        initialState={INITIAL_STATE}
      />
    </StyledStaffWrapper>
  );
};

export default Staff;
